import { IRebaseOptions } from '../models/data/IRebaseOptions';
import { sortByDate } from '../utils/collection';
import { isNullOrEmpty, isNullOrUndefined, isNumber } from '../utils/typeChecks';
import { TRebaseModifier } from '../models/data/TRebaseModifier';
import { IRebaseSeries } from '../models/data/IRebaseSeries';
import { ITransformOptions } from '../models/query/transforms';
import { Transform } from '../query/transform/Transform';
import { TranslationHelper } from '.';

export class RebaseHelper {
	options: IRebaseOptions;
	translationHelper?: TranslationHelper;

	constructor(options: IRebaseOptions, translationHelper?: TranslationHelper) {
		this.options = options;
		this.translationHelper = translationHelper || new TranslationHelper({}, {});
	}

	/**
	 * Check whether rebase should be applied
	 * @param collection
	 */
	mustPerformRebase(collection: any[]) {
		const { enabled, series } = this.options;
		return enabled && !isNullOrEmpty(series) && !isNullOrEmpty(collection);
	}

	/**
	 * Apply rebase to a data collection
	 * @param collection
	 */
	rebase(collection: any[]) {
		const { series, postTransforms } = this.options;
		if (this.mustPerformRebase(collection)) {
			collection = sortByDate(collection, (item: any) => item.date);
			collection = this.applyInceptionInjection(collection);
			series.forEach((seriesInstance: IRebaseSeries) => {
				collection = this.orchestrateSeries(collection, seriesInstance);
				collection = this.rebaseSeries(collection, seriesInstance);
				if (!isNullOrEmpty(postTransforms)) {
					collection = this.postTransforms(collection, seriesInstance, postTransforms);
				}
			});
		}
		return collection;
	}

	applyInceptionInjection(collection: any[]): any[] {
		const { inceptionInjection, series } = this.options;
		if (inceptionInjection) {
			if (!series.some(serie => serie.type === 'value')) {
				const { enabled, forceUniqueInceptionRecord, inceptionDate } = inceptionInjection;
				if (enabled) {
					const mustInject = !forceUniqueInceptionRecord || !collection.some(item => item.date === inceptionDate);
					if (mustInject) {
						// Get the first item's date prop to determine the data type
						const itemDate = collection[0].date;
						let typedInceptionDate;
						switch (typeof itemDate) {
							case 'number':
								typedInceptionDate = new Date(inceptionDate).getTime();
								break;
							case 'object':
								// Check if date object
								if (typeof itemDate.getDate === 'function') {
									typedInceptionDate = new Date(inceptionDate);
								}
								break;
							case 'string':
							default:
								typedInceptionDate = inceptionDate;
								break;
						}
						collection.unshift({ date: typedInceptionDate });
					}
				}
			}
			else {
				console.warn('"Inception injection" configuration is not supported with "value" type series');
			}
		}
		return collection;
	}

	/**
	 * Convert series values to change values if the series type is 'value'
	 * Apply the value modifier if one exists
	 * @param collection
	 * @param series
	 */
	orchestrateSeries(collection: any[], series: IRebaseSeries) {
		const { key, valueModifier, hideNoData = false, type = 'change' } = series;
		if (!isNullOrEmpty(collection)) {

			if (type === 'value' && valueModifier) {
				console.warn(`"valueModifier" configuration cannot be used with "value" type series ~ data key "${key}" is of type "value"`);
			}

			for (let i = collection.length - 1; i > 0; i--) {
				const currentValue = collection[i][key];
				const previousValue = collection[i - 1][key];
				if (type === 'value' && previousValue !== 0) {
					collection[i][key] = (currentValue / previousValue) - 1;
				}
				else if (!isNullOrUndefined(valueModifier)) {
					collection[i][key] = this.applyModifier(currentValue, valueModifier, hideNoData);
				}
			}
		}
		return collection;
	}

	/**
	 * Run the base logic through the values in a series
	 * @param collection
	 * @param key
	 */
	rebaseSeries(collection: any[], series: IRebaseSeries) {
		const { key, hideNoData = false } = series;
		const startValue = series.startValue || this.options.startValue || 100;
		const startIndex = hideNoData ? this.seriesStartIndex(collection, series) : 1;


		if (!isNullOrEmpty(collection)) {
			collection[startIndex - 1][key] = startValue;

			for (let i = startIndex; i < collection.length; i++) {
				const previous = collection[i - 1];
				const delta = collection[i];

				if (Number.isNaN(delta[key]) || isNullOrUndefined(delta[key])) {
					delta[key] = previous[key];
				}
				else {
					const deltaValue = delta[key];
					delta[key] = previous[key] * (1 + deltaValue);
				}
			}
		}
		return collection;
	}

	/**
	 * Apply the given value modifier
	 * @param rawValue
	 * @param valueModifier
	 */
	applyModifier(rawValue: number, valueModifier: TRebaseModifier, hideNoData: boolean): number {
		if (valueModifier) {
			if (valueModifier === 'divideBy100') {
				return hideNoData && !isNumber(rawValue) ? null : (rawValue / 100);
			}
			if (valueModifier === 'multiplyBy100') {
				return hideNoData && !isNumber(rawValue) ? null : (rawValue * 100);
			}
			return valueModifier(rawValue);
		}
		return rawValue;
	}

	seriesStartIndex(collection: unknown[], series: IRebaseSeries): number | undefined {
		const key = series.key;

		for (let i = 0; i < collection.length; i++) {
			if (isNumber(collection[i][key])) {
				return i;
			}
		}
	}

	postTransforms(collection: any, series: IRebaseSeries, transformOptions: ITransformOptions[]) {
		const { key } = series;
		if (!isNullOrEmpty(collection)) {

			for (let i = 0; i < collection.length; i++) {
				for (const transformOption of transformOptions) {
					const transform = new Transform(transformOption, this.translationHelper);
					collection[i][key] = transform.execute(collection[i][key], { queryOptionsType: 'none' }, {});
				}
			}
		}
		return collection;
	}
}
